// pms-adapters / supabase / ledgerGate — C6 Finance's freshness gate
// (PMS-LEDGER-L0-BRIEF §3, migration 20261004100100).
//
// One RPC, `ledger_day_gate`. A stale feed comes back as a row with `open`
// false and a `reason`; only a missing `fin.feed.gate` (42501) or a broken
// read is an error, so the caller can never mistake a failure for « all clear ».

import {
  err,
  ok,
  type IsoDate,
  type LedgerDayGate,
  type LedgerGate,
  type LedgerGateReason,
  type PropertyCode,
  type Result,
} from '@lemontaigu/pms-core';
import { toPmsError, type SupabaseClient } from './support.js';

/** The RPC's one row, verbatim. */
export interface LedgerDayGateRow {
  day: string;
  terminal_ok: boolean;
  terminal_last_poll_at: string | null;
  mirror_ok: boolean;
  mirror_covered_through: string | null;
  vega_closed: boolean;
  open: boolean;
  postable: boolean;
  reason: string | null;
}

const REASONS: readonly LedgerGateReason[] = [
  'terminal_never_polled',
  'terminal_last_poll_failed',
  'terminal_stale',
  'mirror_not_covered',
  'vega_not_closed',
];

export function toLedgerDayGate(row: LedgerDayGateRow): LedgerDayGate {
  // A reason this build does not know must still CLOSE the gate: an open gate
  // is only ever the database saying so with nothing wrong.
  const known = REASONS.find((r) => r === row.reason) ?? null;
  const unknownReason = row.reason !== null && known === null;
  return {
    day: row.day as IsoDate,
    terminalOk: row.terminal_ok,
    terminalLastPollAt: row.terminal_last_poll_at,
    mirrorOk: row.mirror_ok,
    mirrorCoveredThrough: row.mirror_covered_through,
    vegaClosed: row.vega_closed,
    open: row.open && !unknownReason,
    postable: row.postable && !unknownReason,
    reason: known ?? (unknownReason ? 'terminal_stale' : null),
  };
}

export class SupabaseLedgerGate implements LedgerGate {
  constructor(private readonly db: SupabaseClient) {}

  async day(propertyCode: PropertyCode, day: IsoDate): Promise<Result<LedgerDayGate>> {
    const r = await this.db.rpc('ledger_day_gate', { p_property: propertyCode, p_day: day });
    if (r.error) {
      if (r.error.code === '42501') {
        return err('forbidden', 'Accès refusé — état des flux', { cause: r.error });
      }
      return { ok: false, error: toPmsError(r.error) };
    }
    const rows = r.data as LedgerDayGateRow[] | null;
    // The function always returns exactly one row. Anything else is not an
    // answer, and must not read as one.
    if (!Array.isArray(rows) || rows.length !== 1) {
      return err('unknown', 'État des flux : réponse illisible', { retryable: false });
    }
    return ok(toLedgerDayGate(rows[0]!));
  }
}
